import { useState } from "react";
import { Link } from "react-router-dom";
import { Truck, Scales } from "@phosphor-icons/react";
import usePricing from "@/hooks/usePricing";
import { MIRAMAR_RATES, miramarEstimate, curbPrice } from "@/seoContentV3";

const yardsOf = (t) => parseFloat(String(t.yards).replace(/[^0-9.]/g, "")) || 2;

const Card = ({ testid, title, price, children, accent }) => (
  <div data-testid={testid} className={`rounded-sm p-5 border-2 ${accent ? "border-[#46A01E] bg-[#F4FAF0]" : "border-[#E4E4E7] bg-white"}`}>
    <p className="text-xs font-display font-extrabold uppercase tracking-widest text-[#52525B]">{title}</p>
    <p className="font-display font-black text-4xl text-[#15306E] mt-1">{price}</p>
    <div className="text-sm text-[#52525B] mt-2 leading-relaxed">{children}</div>
  </div>
);

export default function MiramarCalculator() {
  const pricing = usePricing();
  const [idx, setIdx] = useState(0);
  const tier = pricing.tiers[Math.min(idx, pricing.tiers.length - 1)];
  const yards = yardsOf(tier);
  const city = miramarEstimate(yards, "city");
  const nonCity = miramarEstimate(yards, "nonCity");
  const trips = city.tier1Trips + city.tier2Trips;
  const curb = curbPrice(tier.price, pricing.curbsideDiscount);

  return (
    <section data-testid="miramar-calculator" className="max-w-4xl mx-auto my-12 border border-[#E4E4E7] rounded-sm p-6 lg:p-8 bg-white">
      <h2 className="font-display font-black tracking-tighter text-2xl lg:text-3xl text-[#15306E]">Miramar dump run vs. pickup calculator</h2>
      <p className="text-[#52525B] mt-2">Pick how much junk you have. We'll compare Miramar Landfill gate fees with our pickup price.</p>

      <div className="flex flex-wrap gap-2 mt-5" role="radiogroup" aria-label="Load size">
        {pricing.tiers.map((t, i) => (
          <button key={t.label} type="button" role="radio" aria-checked={i === idx} data-testid={`calc-load-${i}`} onClick={() => setIdx(i)}
            className={`px-4 py-2 rounded-full text-sm font-display font-bold border-2 transition-colors ${i === idx ? "bg-[#15306E] border-[#15306E] text-white" : "border-[#E4E4E7] text-[#15306E] hover:border-[#46A01E]"}`}>
            {t.label} <span className="font-normal opacity-80">({t.yards})</span>
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-6">
        <Card testid="calc-miramar" title="Miramar gate fees" price={`$${city.fee}`}>
          <p className="flex items-center gap-1.5"><Truck size={16} weight="bold" /> {trips} trip{trips > 1 ? "s" : ""}: {city.tier1Trips ? `${city.tier1Trips} pickup load${city.tier1Trips > 1 ? "s" : ""}` : ""}{city.tier1Trips && city.tier2Trips ? " + " : ""}{city.tier2Trips ? `${city.tier2Trips} trailer/large load${city.tier2Trips > 1 ? "s" : ""}` : ""}</p>
          <p className="mt-1">City rate. Non-city waste: <span data-testid="calc-miramar-noncity">${nonCity.fee}</span>.</p>
          <p className="mt-1">Plus truck or trailer, gas, tarps and about {Math.round(trips * 1.5 * 10) / 10} hours of loading, driving and unloading.</p>
        </Card>
        <Card testid="calc-curbside" title="CleanOut247 curbside" price={`$${curb}`} accent>
          You stage it in the driveway or garage. We load, haul and pay all disposal.
        </Card>
        <Card testid="calc-full" title="CleanOut247 full-service" price={`$${tier.price}`}>
          We carry it out from anywhere, including upstairs and the backyard. Sweep-up included.
        </Card>
      </div>

      <p className="text-xs text-[#71717A] mt-4 flex items-start gap-1.5">
        <Scales size={14} className="mt-0.5 shrink-0" />
        <span>Estimate uses Miramar's FY2027 flat rates for general refuse (Tier 1 ${MIRAMAR_RATES.tier1.city}, about {MIRAMAR_RATES.tier1.yards} cu yds per pickup load; Tier 2 ${MIRAMAR_RATES.tier2.city}, about {MIRAMAR_RATES.tier2.yards} cu yds per trailer load). Mattresses, TVs and appliances can't go in a regular Miramar load. See the <Link to="/junk-removal-cost" className="underline">full price list</Link>.</span>
      </p>
    </section>
  );
}
